// Game state store. The single client-side source of truth for the current
// lobby + game snapshot (populated by WS `state_update`). UI reads from here;
// mutations go through the WS action emitters (server-authoritative).

import { create } from "zustand";
import type { DomainEventLike } from "../lib/events";
import type { GameStateView } from "../lib/types";

export type ConnStatus = "offline" | "reconnecting" | "online";

interface GameState {
  lobbyId: string | null;
  gameState: GameStateView | null;
  conn: ConnStatus;
  pendingAceCardId: string | null; // card awaiting the alto/basso choice
  lastEvents: DomainEventLike[]; // for transient UI cues (toasts/animation)
  kicked: { reason: string } | null;
  lastError: string | null; // last rejected action surfaced by the server

  setLobbyId: (id: string | null) => void;
  setGameState: (s: GameStateView | null) => void;
  setConnection: (c: ConnStatus) => void;
  setPendingAce: (cardId: string | null) => void;
  ingestEvents: (e: DomainEventLike[]) => void;
  markKicked: (reason: string) => void;
  setError: (message: string | null) => void;
  reset: () => void;
}

export const useGameStore = create<GameState>((set) => ({
  lobbyId: null,
  gameState: null,
  conn: "offline",
  pendingAceCardId: null,
  lastEvents: [],
  kicked: null,
  lastError: null,
  setLobbyId: (id) => set({ lobbyId: id, gameState: id ? null : null }),
  setGameState: (s) => set({ gameState: s, lobbyId: s?.lobby_id ?? null, kicked: s ? null : null }),
  setConnection: (c) => set({ conn: c }),
  setPendingAce: (cardId) => set({ pendingAceCardId: cardId }),
  ingestEvents: (e) => set({ lastEvents: e }),
  markKicked: (reason) => set({ kicked: { reason } }),
  setError: (message) => set({ lastError: message }),
  reset: () => set({ lobbyId: null, gameState: null, conn: "offline", pendingAceCardId: null, kicked: null, lastError: null }),
}));
